import React from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import { BLACKOUT_SCOPES, SCOPE_TAG } from "helpers/blackouts"
import styles from "./index.module.css"

/** Scope -> the DSIcon whose glyph matches the design's tag icon. */
const SCOPE_ICON = {
  [BLACKOUT_SCOPES.BUSINESS]: "blackout",
  [BLACKOUT_SCOPES.LISTING]: "experiences",
  [BLACKOUT_SCOPES.CREW]: "users",
  [BLACKOUT_SCOPES.EQUIPMENT]: "box"
}

/**
 * The blue pill carries the design's own crew glyph — the captain's cap from Figma 6372:10172,
 * which the dark pill's `users` group icon does not depict. Only crew differs; the other scopes
 * use one glyph across both tones.
 */
const BRAND_SCOPE_ICON = { ...SCOPE_ICON, [BLACKOUT_SCOPES.CREW]: "captain" }

/**
 * The scope pill — Business / Listings / Crew / Equipment.
 *
 * Three tones, one per ground the pill sits on:
 *  - `soft`  — the pastel warning pill, keyed to the scope's own colour
 *  - `solid` — the dark pill on the calendar day panel, beside a date on a white card
 *  - `brand` — the blue pill on the Resources rows (Figma 6372:10172), where the scope is the
 *              row's subject rather than a warning about it
 *
 * The dark pill stands alone, so it spells the scope out ("Equipment Blackout"); the other two
 * sit in context and keep the bare noun.
 */
const BlackoutScopeTag = ({ scope, label, tone = "soft", showIcon = true, className = "" }) => {
  const { t } = useTranslation()
  const tag = SCOPE_TAG[scope]

  if (!tag) return null

  const isSolid = tone === "solid"
  // Only the pastel warning pill uses the alert glyph; the other tones name the scope itself.
  const isWarningTone = tone === "soft"

  const getIconName = () => {
    if (isWarningTone) return "AlertTriangle"
    return tone === "brand" ? BRAND_SCOPE_ICON[scope] : SCOPE_ICON[scope]
  }

  const fallbackLabel = isSolid
    ? t(tag.solidLabelKey, { defaultValue: tag.solidLabelDefault })
    : t(tag.labelKey, { defaultValue: tag.labelDefault })

  return (
    <span className={`${styles.tag} ${isWarningTone ? styles[tag.variant] : styles[tone]} ${className}`}>
      {showIcon && (
        <Icon
          type={isWarningTone ? "feather" : "ds"}
          name={getIconName()}
          width={isWarningTone ? 12 : 16}
          height={isWarningTone ? 12 : 16}
          color="currentColor"
        />
      )}
      <span className={styles.label}>{label || fallbackLabel}</span>
    </span>
  )
}

BlackoutScopeTag.propTypes = {
  scope: PropTypes.string.isRequired,
  /** Overrides the scope's default copy, e.g. "All Experiences Blackout". */
  label: PropTypes.string,
  tone: PropTypes.oneOf(["soft", "solid", "brand"]),
  showIcon: PropTypes.bool,
  className: PropTypes.string
}

export default BlackoutScopeTag
